import {I18nKeys} from '@multipass-i18n';
import {Badge, buttonClassName, Container, Metric} from '@multipass-ui-components';
import * as React from 'react';

import {I18nKey, useT} from '../../logic';
import {PortalFooter} from '../shell/PortalFooter';
import {RouterLink} from '../shell/RouterLink';
import {HowItWorksSection} from './HowItWorksSection';
import {SocialProof} from './SocialProof';

const btnPrimaryLg = buttonClassName({size: 'lg'});
const btnSecondaryLg = buttonClassName({variant: 'secondary', size: 'lg'});

const SectionHead: React.FunctionComponent<React.PropsWithChildren<{title: string}>> = ({title, children}: React.PropsWithChildren<{title: string}>) => (
    <div className="mx-auto mb-10 max-w-[60ch] text-center">
        <h2 className="mb-3 text-headline">{title}</h2>
        {children && <p className="text-text-secondary">{children}</p>}
    </div>
);

const Hero: React.FunctionComponent = () => {
    const t = useT();
    return (
        <section className="pb-16 pt-20 text-center">
            <Container>
                <Badge variant="brand" className="mb-5">
                    {t(I18nKeys.landing.heroBadge)}
                </Badge>
                <h1 className="mx-auto mb-4 max-w-[15ch] text-display font-bold">
                    {t(I18nKeys.landing.heroTitle)}
                </h1>
                <p className="mx-auto mb-6 max-w-[56ch] text-body-lg text-text-secondary">
                    {t(I18nKeys.landing.heroBody)}
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                    <RouterLink to="/signup" className={btnPrimaryLg}>{t(I18nKeys.common.startFree)}</RouterLink>
                    <RouterLink to="/how-it-works" className={btnSecondaryLg}>{t(I18nKeys.landing.heroCtaSecondary)}</RouterLink>
                </div>
                <p className="mt-4 text-caption text-text-muted">{t(I18nKeys.landing.heroFootnote)}</p>
            </Container>
        </section>
    );
};

const metrics: {valueKey: I18nKey; labelKey: I18nKey}[] = [
    {valueKey: I18nKeys.landing.metricSkillsValue, labelKey: I18nKeys.landing.metricSkillsLabel},
    {valueKey: I18nKeys.landing.metricSessionValue, labelKey: I18nKeys.landing.metricSessionLabel},
    {valueKey: I18nKeys.landing.metricSignalValue, labelKey: I18nKeys.landing.metricSignalLabel},
    {valueKey: I18nKeys.landing.metricPlanValue, labelKey: I18nKeys.landing.metricPlanLabel},
];

const MetricsBand: React.FunctionComponent = () => {
    const t = useT();
    return (
        <section className="bg-surface-muted py-16">
            <Container>
                <SectionHead title={t(I18nKeys.landing.metricsTitle)}>
                    {t(I18nKeys.landing.metricsBody)}
                </SectionHead>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-6">
                    {metrics.map(({valueKey, labelKey}) => (
                        <Metric key={labelKey} value={t(valueKey)} label={t(labelKey)}/>
                    ))}
                </div>
            </Container>
        </section>
    );
};

const CtaBand: React.FunctionComponent = () => {
    const t = useT();
    return (
        <section id="trial" className="border-t border-border-subtle bg-surface py-16 text-center">
            <Container>
                <h2 className="mb-3 text-headline">{t(I18nKeys.landing.ctaTitle)}</h2>
                <p className="mx-auto mb-6 max-w-[48ch] text-text-secondary">
                    {t(I18nKeys.landing.ctaBody)}
                </p>
                <RouterLink to="/signup" className={btnPrimaryLg}>{t(I18nKeys.common.createFreeAccount)}</RouterLink>
            </Container>
        </section>
    );
};

export const LandingPage: React.FunctionComponent = () => (
    <>
        <Hero/>
        <HowItWorksSection/>
        <MetricsBand/>
        <SocialProof/>
        <CtaBand/>
        <PortalFooter crossLink="howItWorks"/>
    </>
);
